import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { cn } from "@/lib/utils";

import type { HorizontalScrollProps, HorizontalScrollRef } from "./lib";
import { useHorizontalScrollDrag } from "./use-horizontal-scroll-drag";

export const HorizontalScroll = forwardRef(function HorizontalScroll<T>(
  {
    data,
    itemSpacing = 0,
    contentPadding = { start: 0, end: 0 },
    itemWidth = 200,
    onScrollIndexChange,
    initialIndex = 0,
    renderItem,
    keyExtractor,
    snap = "none",
    className,
  }: HorizontalScrollProps<T>,
  ref: React.Ref<HorizontalScrollRef>,
) {
  const scrollRef = useHorizontalScrollDrag();
  const currentIndexRef = useRef<number>(initialIndex);
  const hasInitializedRef = useRef(false);
  const scrollStep = itemWidth + itemSpacing;
  const calculateCurrentIndex = useCallback(
    (scrollLeft: number): number => {
      const adjustedScroll = scrollLeft - contentPadding.start;
      const index = Math.round(adjustedScroll / scrollStep);
      return Math.max(0, Math.min(index, data.length - 1));
    },
    [contentPadding.start, data.length, scrollStep],
  );
  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = "smooth") => {
      const el = scrollRef.current;
      if (!el) return;
      const clamped = Math.max(0, Math.min(index, data.length - 1));
      const left = contentPadding.start + clamped * scrollStep;
      el.scrollTo({ left, behavior });
      currentIndexRef.current = clamped;
    },
    [contentPadding.start, data.length, scrollStep],
  );
  useImperativeHandle(
    ref,
    () => ({
      next: () => {
        const el = scrollRef.current;
        if (!el) return;
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        if (el.scrollLeft < maxScroll) {
          el.scrollBy({ left: scrollStep, behavior: "smooth" });
        }
      },
      prev: () => {
        const el = scrollRef.current;
        if (!el) return;
        if (el.scrollLeft > 0) {
          el.scrollBy({ left: -scrollStep, behavior: "smooth" });
        }
      },
      scrollToIndex: (index: number) => scrollToIndex(index),
      getCurrentIndex: () => currentIndexRef.current,
    }),
    [scrollStep, scrollToIndex],
  );
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || hasInitializedRef.current) return;
    if (initialIndex > 0 && initialIndex < data.length) {
      const left = contentPadding.start + initialIndex * scrollStep;
      el.scrollTo({ left, behavior: "instant" });
    }
    hasInitializedRef.current = true;
  }, [scrollRef, initialIndex, contentPadding.start, data.length, scrollStep]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScrollEnd = () => {
      const newIndex = calculateCurrentIndex(el.scrollLeft);
      const prev = currentIndexRef.current;
      currentIndexRef.current = newIndex;
      if (newIndex !== prev) onScrollIndexChange?.(newIndex);
    };
    el.addEventListener("scrollend", handleScrollEnd);
    return () => el.removeEventListener("scrollend", handleScrollEnd);
  }, [scrollRef, onScrollIndexChange, calculateCurrentIndex]);
  const contentContainerStyle: React.CSSProperties = {
    gap: itemSpacing,
    display: "flex",
    flexDirection: "row",
    overflowX: "hidden",
    paddingLeft: contentPadding.start,
    paddingRight: contentPadding.end,
    scrollPaddingLeft: contentPadding.start,
    scrollPaddingRight: contentPadding.end,
    scrollSnapType: snap !== "none" ? "x mandatory" : undefined,
  };
  const getScrollSnapAlign = (): "start" | "end" | "center" | undefined => {
    if (snap === "none") return undefined;
    if (snap === "middle") return "center";
    return snap;
  };
  const itemStyle: React.CSSProperties = {
    width: `${itemWidth}px`,
    minWidth: `${itemWidth}px`,
    maxWidth: `${itemWidth}px`,
    scrollSnapAlign: getScrollSnapAlign(),
  };
  return (
    <div className={cn("flex flex-col", className)}>
      <div ref={scrollRef} style={contentContainerStyle}>
        {data.map((item, idx) => {
          const key = keyExtractor ? keyExtractor(item, idx) : idx;
          return (
            <div key={key} style={itemStyle}>
              {renderItem(item, idx)}
            </div>
          );
        })}
      </div>
    </div>
  );
}) as <T>(
  props: HorizontalScrollProps<T> & { ref?: React.Ref<HorizontalScrollRef> },
) => React.ReactElement | null;
